import { GRANULARITY } from './deriv-feed.js';

// Public chart data used by https://hw.online/trading-instruments/vol_80/.
// The symbol metadata specifies pricescale=1: one integer index point.
const API = 'https://webcart.trade-hw.online/api/getBars';
const RESOLUTION = Object.freeze({ M1: '1', M5: '5', M15: '15', M30: '30', H1: '60', H4: '240', D1: '1D' });
export const HEADWAY_REFRESH_MS = 5000;

export function decodeHeadwayCandles(rows, now = Date.now()) {
  if (!Array.isArray(rows) || !rows.length) throw new Error('Sejarah VOL 80 belum tersedia.');
  const candles = new Map();
  for (const row of rows) {
    const time = row.time * 1000;
    if (!Number.isSafeInteger(row.time) || time <= 0 || time > now + 10000 ||
        ![row.open, row.high, row.low, row.close].every(price => Number.isFinite(price) && price > 0) ||
        row.low > Math.min(row.open, row.close) || row.high < Math.max(row.open, row.close) ||
        !Number.isFinite(row.volume) || row.volume < 0) throw new Error('Data candle VOL 80 tidak sah.');
    const next = { time, open: row.open, high: row.high, low: row.low, close: row.close, volume: row.volume };
    const previous = candles.get(time);
    // Headway can return both a partial and a fuller snapshot for one timestamp.
    // Keep the fuller snapshot, never draw overlapping duplicate candles.
    if (!previous || next.volume >= previous.volume) candles.set(time, next);
  }
  return [...candles.values()].sort((a, b) => a.time - b.time);
}

export async function loadHeadwayCandles(symbol, tf, { fetchImpl = fetch, signal, now = Date.now() } = {}) {
  if (symbol !== 'VOL80' || !Object.hasOwn(RESOLUTION, tf)) throw new Error('Unsupported Headway market request.');
  const count = tf === 'H4' || tf === 'D1' ? 300 : 600;
  const requestSignal = signal ? AbortSignal.any([signal, AbortSignal.timeout(8000)]) : AbortSignal.timeout(8000);
  async function bars(frame, countBack) {
    const to = Math.floor(now / 1000);
    const url = new URL(API);
    url.search = new URLSearchParams({ symbol: 'VOL_80', resolution: RESOLUTION[frame],
      from: String(to - (countBack + 2) * GRANULARITY[frame]), to: String(to), countback: String(countBack) });
    const response = await fetchImpl(url, { signal: requestSignal, cache: 'no-store' });
    if (!response.ok) throw new Error('Sambungan candle VOL 80 terganggu. Cuba semula.');
    return decodeHeadwayCandles(await response.json(), now);
  }
  const [history, recent] = await Promise.all([bars(tf, count), tf === 'M1' ? Promise.resolve(null) : bars('M1', 3)]);
  if (history.length < 2) throw new Error('Sejarah VOL 80 belum mencukupi.');
  const candles = history.slice(-count);
  const lastMinute = (recent || history).at(-1);
  const latest = candles.at(-1);
  const period = GRANULARITY[tf] * 1000;
  // The public quote summary and higher-timeframe close can lag the actual M1
  // candles. Update only the matching current bar from newer real OHLC data.
  // Do not turn a sampled candle into a tick or manufacture a bid/ask spread.
  const matchesLatest = lastMinute.time >= latest.time && lastMinute.time < latest.time + period;
  if (recent && matchesLatest) {
    const within = recent.filter(bar => bar.time >= latest.time);
    candles[candles.length - 1] = { ...latest, close: lastMinute.close,
      high: Math.max(latest.high, ...within.map(bar => bar.high)),
      low: Math.min(latest.low, ...within.map(bar => bar.low)) };
  }
  return { symbol, source: 'Headway', priceType: 'candle', sessionOffsetMinutes: 0,
    timeframe: tf, fetchedAt: new Date(now).toISOString(), latestCandleTime: matchesLatest ? lastMinute.time : latest.time, candles };
}
